import { Component, computed, inject, isDevMode, viewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { HlmAccordionImports } from '@spartan-ng/helm/accordion';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { BrnDialogRef, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { DueDatePickerComponent } from '../../molecules/due-date-picker/due-date-picker.component';
import { MemberMultiselectInputComponent } from '../../molecules/member-multiselect-input/member-multiselect-input.component';
import { PriorityDropdownComponent } from '../../molecules/priority-dropdown/priority-dropdown.component';
import { PriorityValue } from '../../molecules/priority-dropdown/priority-dropdown.interfaces';
import { StatusDropdownComponent } from '../../molecules/status-dropdown/status-dropdown.component';
import { StatusValue } from '../../molecules/status-dropdown/status-dropdown.interfaces';
import { TagsInputComponent } from '../../molecules/tags-input/tags-input.component';
import {
  CreateTaskAssigneeOption,
  CreateTaskFormDialogData,
  CreateTaskFormResult,
} from './create-task-form.interface';
import { SceneSetupPayload, UrlFlagsService } from 'shared';
import { SceneThumbnailComponent } from '../../molecules/scene-thumbnail/scene-thumbnail.component';
import { ThemedIconComponent } from '../../atoms/themed-icon/themed-icon.component';
import { CreateTaskFormStore } from './create-task-form.store';
import { joinTags, splitTagList, throttle } from 'utilities';

@Component({
  selector: 'ocp-create-task-form',
  templateUrl: './create-task-form.component.html',
  styleUrls: ['./create-task-form.component.scss'],
  providers: [CreateTaskFormStore],
  imports: [
    HlmAccordionImports,
    HlmButtonImports,
    ThemedIconComponent,
    FormsModule,
    TranslatePipe,
    MemberMultiselectInputComponent,
    PriorityDropdownComponent,
    StatusDropdownComponent,
    DueDatePickerComponent,
    SceneThumbnailComponent,
    TagsInputComponent,
  ],
})
export class CreateTaskFormComponent {
  private readonly form = viewChild.required<NgForm>('createTaskForm');
  private readonly dialogRef = inject<BrnDialogRef<CreateTaskFormResult | null>>(BrnDialogRef, {
    optional: true,
  });
  private readonly dialogContext = injectBrnDialogContext<CreateTaskFormDialogData>({
    optional: true,
  });
  private readonly urlFlagsService = inject(UrlFlagsService);
  readonly store = inject(CreateTaskFormStore);

  protected readonly isEmbedded = this.urlFlagsService.embedded;

  readonly assigneeOptions: CreateTaskAssigneeOption[] = this.dialogContext?.assigneeOptions ?? [];
  readonly memberDropdownOptions = this.store.memberDropdownOptions(this.assigneeOptions);

  protected readonly tags = computed(() => splitTagList(this.store.tags()));

  /** Immediate action with cooldown to prevent duplicate add-scene calls. */
  @throttle(undefined, isDevMode())
  protected onAddSceneThrottled(): void {
    this.onAddScene();
  }

  constructor() {
    this.store.tags.set(this.dialogContext?.classification ?? '');
  }

  protected onSubmit(): void {
    if (!this.validateForm()) {
      return;
    }
    const result: CreateTaskFormResult = {
      title: this.store.title().trim(),
      description: this.store.description().trim(),
      assignees: this.store.assignees(),
      watchers: this.store.watchers(),
      priority: this.store.priority(),
      status: this.store.status() ?? 'Open',
      dueDate: this.store.dueDate(),
      scene: this.store.scene() ?? undefined,
      classification: this.store.tags().trim(),
    };
    this.dialogRef?.close(result);
  }

  protected onAddScene(): void {
    const onAddScene = this.dialogContext?.onAddScene;
    if (onAddScene) {
      onAddScene((payload: SceneSetupPayload) => this.store.scene.set(payload));
    }
  }

  protected onRemoveScene(): void {
    this.store.scene.set(null);
  }

  protected onTagsChange(tags: string[]): void {
    this.store.tags.set(joinTags(tags));
  }

  protected onAssigneesChange(assigneeIds: string[]): void {
    const selected = assigneeIds
      .map(id => this.assigneeOptions.find(option => option.id === id))
      .filter((o): o is CreateTaskAssigneeOption => !!o);
    this.store.assignees.set(selected);
  }

  protected onWatchersChange(watcherIds: string[]): void {
    const selected = watcherIds
      .map(id => this.assigneeOptions.find(option => option.id === id))
      .filter((o): o is CreateTaskAssigneeOption => !!o);
    this.store.watchers.set(selected);
  }

  protected onMoreDetailsOpenedChange(opened: boolean): void {
    this.store.showMoreDetails.set(opened);
  }

  protected onPriorityChange(priority: PriorityValue): void {
    this.store.priority.set(priority);
  }

  protected onStatusChange(status: StatusValue): void {
    this.store.status.set(status ?? 'Open');
  }

  protected onDueDateChange(date: Date | null): void {
    this.store.dueDate.set(date);
  }

  protected onCancel(): void {
    this.dialogRef?.close();
  }

  protected submitForm(): void {
    this.onSubmit();
  }

  private validateForm(): boolean {
    const form = this.form();
    Object.values(form.controls).forEach(control => control.markAsTouched());
    return !form.invalid;
  }
}
